import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BarChart as BarChartIcon, Table as TableIcon, Activity, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, ScatterChart, Scatter } from 'recharts';
import { cn } from '@/lib/utils';
import { useState, useMemo } from 'react';

export function DataProfileReport({ open, onOpenChange, data }: any) {
    const [selectedCol, setSelectedCol] = useState<any>(data?.columns?.[0] || null);
    const [bivariate, setBivariate] = useState<{ x: string | null, y: string | null, score: number | null }>({ x: null, y: null, score: null });

    // Sort columns by Health (Nulls Descending to highlight issues)
    const sortedCols = useMemo(() => {
        if (!data?.columns) return [];
        return [...data.columns].sort((a, b) => b.nullCount - a.nullCount);
    }, [data]);

    // Update selected if null (on first load)
    if (!selectedCol && sortedCols.length > 0) setSelectedCol(sortedCols[0]);

    if (!data) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[90vw] h-[90vh] flex flex-col p-0 gap-0">
                <DialogHeader className="px-6 py-4 border-b bg-gray-50 flex-shrink-0">
                    <div className="flex justify-between items-center">
                        <div>
                            <DialogTitle className="text-xl">Data Profile Report</DialogTitle>
                            <DialogDescription className="mt-1 flex gap-4 text-sm">
                                <span><span className="font-semibold text-gray-900">{data.totalRows.toLocaleString()}</span> Rows</span>
                                <span><span className="font-semibold text-gray-900">{data.totalColumns}</span> Columns</span>
                                <span className={cn("font-bold px-2 py-0.5 rounded text-xs",
                                    data.healthScore > 80 ? "bg-green-100 text-green-700" :
                                        data.healthScore > 50 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700")}>
                                    Health Score: {data.healthScore}/100
                                </span>
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex flex-1 overflow-hidden">
                    {/* LEFT SIDEBAR: Column List */}
                    <div className="w-[300px] border-r bg-gray-50/50 flex flex-col">
                        <div className="p-3 border-b text-xs font-semibold text-gray-500 uppercase tracking-wider">Columns</div>
                        <ScrollArea className="flex-1">
                            <div className="flex flex-col">
                                {sortedCols.map((col: any) => {
                                    const nullPct = (col.nullCount / data.totalRows) * 100;
                                    const isError = nullPct > 5;
                                    return (
                                        <button
                                            key={col.name}
                                            onClick={() => setSelectedCol(col)}
                                            className={cn("text-left px-4 py-3 hover:bg-gray-100 border-b border-gray-100 transition-colors flex flex-col gap-1",
                                                selectedCol?.name === col.name && "bg-indigo-50 border-l-4 border-l-indigo-600 border-b-indigo-50"
                                            )}
                                        >
                                            <div className="flex justify-between items-center w-full">
                                                <span className="font-medium text-sm truncate">{col.name}</span>
                                                <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 rounded">{col.type}</span>
                                            </div>
                                            {/* Health Bar */}
                                            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mt-1 flex">
                                                <div
                                                    className={cn("h-full", isError ? "bg-red-500" : "bg-green-500")}
                                                    style={{ width: `${100 - nullPct}%` }}
                                                />
                                            </div>
                                            {nullPct > 0 && <span className="text-[10px] text-red-500">{nullPct.toFixed(1)}% Missing</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        </ScrollArea>
                    </div>

                    {/* RIGHT MAIN PANEL */}
                    <div className="flex-1 bg-white flex flex-col overflow-hidden min-h-0">
                        <Tabs defaultValue="overview" className="flex-1 flex flex-col min-h-0">
                            <div className="border-b px-6 pt-2">
                                <TabsList>
                                    <TabsTrigger value="overview">Column Overview</TabsTrigger>
                                    <TabsTrigger value="correlations">Correlations</TabsTrigger>
                                </TabsList>
                            </div>

                            {/* TAB: OVERVIEW */}
                            <TabsContent value="overview" className="flex-1 overflow-y-auto p-6 m-0">
                                {selectedCol && (
                                    <div className="space-y-8 animate-in fade-in duration-300">
                                        {/* Header Stats */}
                                        <div className="grid grid-cols-4 gap-4">
                                            <StatCard label="Type" value={selectedCol.type} icon={TableIcon} />
                                            <StatCard label="Unique Values" value={selectedCol.uniqueCount.toLocaleString()} icon={Activity} />
                                            <StatCard
                                                label="Missing"
                                                value={`${selectedCol.nullCount} (${((selectedCol.nullCount / data.totalRows) * 100).toFixed(1)}%)`}
                                                icon={AlertTriangle}
                                                alert={selectedCol.nullCount > 0}
                                            />
                                            <StatCard label="Validity" value="100%" icon={CheckCircle2} />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 h-[400px]">
                                            {/* Numeric: Histogram */}
                                            {selectedCol.histogram ? (
                                                <div className="border rounded-lg p-4 shadow-sm flex flex-col">
                                                    <h3 className="font-semibold mb-4 text-sm text-gray-700 flex items-center gap-2">
                                                        <BarChartIcon className="w-4 h-4" /> Distribution
                                                    </h3>
                                                    <div className="flex-1 w-full min-h-0">
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <BarChart data={selectedCol.histogram}>
                                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                                                <XAxis
                                                                    dataKey="label"
                                                                    tick={{ fontSize: 10 }}
                                                                    interval={0}
                                                                    angle={-45}
                                                                    textAnchor="end"
                                                                    height={60}
                                                                />
                                                                <YAxis tick={{ fontSize: 10 }} />
                                                                <Tooltip
                                                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                                                    cursor={{ fill: '#F3F4F6' }}
                                                                />
                                                                <Bar dataKey="count" fill="#4F46E5" radius={[4, 4, 0, 0]}>
                                                                    {selectedCol.histogram.map((entry: any, index: number) => (
                                                                        <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#4F46E5' : '#6366F1'} />
                                                                    ))}
                                                                </Bar>
                                                            </BarChart>
                                                        </ResponsiveContainer>
                                                    </div>
                                                </div>
                                            ) : (
                                                /* Categorical: Top Values */
                                                <div className="border rounded-lg p-4 shadow-sm">
                                                    <h3 className="font-semibold mb-4 text-sm text-gray-700">Top Values</h3>
                                                    <div className="space-y-2">
                                                        {selectedCol.topValues && Object.entries(selectedCol.topValues).map(([val, count]: any, i: number) => (
                                                            <div key={val} className="flex items-center gap-2 text-sm">
                                                                <span className="w-6 text-gray-400 font-mono text-xs">{i + 1}.</span>
                                                                <div className="flex-1">
                                                                    <div className="flex justify-between mb-1">
                                                                        <span className="font-medium truncate max-w-[150px]">{val}</span>
                                                                        <span className="text-gray-500">{count}</span>
                                                                    </div>
                                                                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                                        <div className="h-full bg-indigo-500" style={{ width: `${(count / data.totalRows) * 100}%` }} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Stats Detail */}
                                            <div className="border rounded-lg p-4 shadow-sm">
                                                <h3 className="font-semibold mb-4 text-sm text-gray-700">Statistics</h3>
                                                {selectedCol.min !== null ? (
                                                    <div className="grid grid-cols-2 gap-y-4 text-sm">
                                                        <div><span className="text-gray-500 block text-xs">Min</span>{selectedCol.min?.toFixed(2)}</div>
                                                        <div><span className="text-gray-500 block text-xs">Max</span>{selectedCol.max?.toFixed(2)}</div>
                                                        <div><span className="text-gray-500 block text-xs">Mean</span>{selectedCol.mean?.toFixed(2)}</div>
                                                        <div><span className="text-gray-500 block text-xs">Median</span>{selectedCol.median?.toFixed(2)}</div>
                                                        <div><span className="text-gray-500 block text-xs">Std Dev</span>{selectedCol.stdDev?.toFixed(2)}</div>
                                                        <div className={cn(selectedCol.outlierCount > 0 ? "text-red-600" : "")}>
                                                            <span className="text-gray-500 block text-xs">Outliers</span>
                                                            {selectedCol.outlierCount}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-gray-400 italic text-sm">Numeric statistics not applicable.</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </TabsContent>

                            {/* TAB: CORRELATIONS */}
                            <TabsContent value="correlations" className="flex-1 overflow-y-auto min-h-0 p-6 m-0">
                                <div className="flex flex-col gap-6 pb-24">
                                    <div className="flex-shrink-0">
                                        <h3 className="text-lg font-semibold mb-4">Correlation Matrix</h3>
                                        {data.correlations && data.correlations.length > 0 ? (
                                            <CorrelationMatrix
                                                correlations={data.correlations}
                                                onSelect={(c1, c2, val) => setBivariate({ x: c1, y: c2, score: val })}
                                                selection={bivariate}
                                            />
                                        ) : (
                                            <div className="text-center p-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed">
                                                Not enough numeric columns for correlation analysis.
                                            </div>
                                        )}
                                    </div>

                                    {/* Bivariate Analysis Area */}
                                    <div className="flex-1 border-t pt-6 flex flex-col min-h-[400px]">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold">Bivariate Analysis</h3>
                                            {bivariate.score !== null && (
                                                <span className="text-sm font-medium bg-gray-100 px-2 py-1 rounded">
                                                    Pearson Score: <span className={cn(bivariate.score > 0 ? "text-blue-600" : "text-red-600")}>{bivariate.score.toFixed(2)}</span>
                                                    <span className="text-gray-400 ml-2 text-xs">({data.sampleData?.length || 0} samples)</span>
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex gap-4 mb-4">
                                            <div className="w-[200px]">
                                                <label className="text-xs font-semibold text-gray-500 uppercase">X-Axis</label>
                                                <select
                                                    className="w-full mt-1 border rounded px-2 py-1 text-sm bg-white"
                                                    value={bivariate.x || ''}
                                                    onChange={e => setBivariate(prev => ({ ...prev, x: e.target.value, score: null }))}
                                                >
                                                    {data.columns.map((c: any) => <option key={c.name} value={c.name}>{c.name}</option>)}
                                                </select>
                                            </div>
                                            <div className="flex items-center text-gray-400"><Activity className="w-4 h-4" /></div>
                                            <div className="w-[200px]">
                                                <label className="text-xs font-semibold text-gray-500 uppercase">Y-Axis</label>
                                                <select
                                                    className="w-full mt-1 border rounded px-2 py-1 text-sm bg-white"
                                                    value={bivariate.y || ''}
                                                    onChange={e => setBivariate(prev => ({ ...prev, y: e.target.value, score: null }))}
                                                >
                                                    {data.columns.map((c: any) => <option key={c.name} value={c.name}>{c.name}</option>)}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="h-[400px] bg-gray-50 rounded-lg border p-4 relative">
                                            {bivariate.x && bivariate.y && data.sampleData ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis type="number" dataKey={bivariate.x} name={bivariate.x} label={{ value: bivariate.x, position: 'bottom', offset: 0 }} />
                                                        <YAxis type="number" dataKey={bivariate.y} name={bivariate.y} label={{ value: bivariate.y, angle: -90, position: 'left' }} />
                                                        <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ active, payload }) => {
                                                            if (active && payload && payload.length) {
                                                                return (
                                                                    <div className="bg-white border shadow-sm p-2 rounded text-xs">
                                                                        <p>{`${bivariate.x}: ${payload[0].value}`}</p>
                                                                        <p>{`${bivariate.y}: ${payload[1].value}`}</p>
                                                                    </div>
                                                                );
                                                            }
                                                            return null;
                                                        }} />
                                                        <Scatter name="Data" data={data.sampleData} fill="#8884d8" />
                                                    </ScatterChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                                                    Select columns to view relationship
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function StatCard({ label, value, icon: Icon, alert }: any) {
    return (
        <div className={cn("bg-white p-4 rounded-lg border shadow-sm flex items-start justify-between", alert ? "border-red-200 bg-red-50" : "")}>
            <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
                <p className={cn("text-2xl font-bold mt-1", alert ? "text-red-700" : "text-gray-900")}>{value}</p>
            </div>
            <div className={cn("p-2 rounded-lg", alert ? "bg-red-100 text-red-600" : "bg-indigo-50 text-indigo-600")}>
                <Icon className="w-5 h-5" />
            </div>
        </div>
    );
}

function CorrelationMatrix({ correlations, onSelect, selection }: any) {
    const uniqueCols = Array.from(new Set(correlations.map((c: any) => c.column1))) as string[];

    return (
        <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
                <div className="flex">
                    {/* Top Headers */}
                    <div className="w-24 flex-shrink-0"></div>
                    {uniqueCols.map(col => (
                        <div key={col} className="w-16 h-24 flex items-end justify-center pb-2">
                            <span className="text-xs text-gray-500 font-medium -rotate-45 whitespace-nowrap origin-bottom-left translate-y-4 translate-x-4">{col}</span>
                        </div>
                    ))}
                </div>

                {uniqueCols.map(rowCol => (
                    <div key={rowCol} className="flex items-center">
                        {/* Row Header */}
                        <div className="w-24 h-12 flex items-center justify-end pr-3 text-xs font-medium text-gray-600 truncate" title={rowCol}>
                            {rowCol}
                        </div>
                        {/* Cells */}
                        {uniqueCols.map(colCol => {
                            const cell = correlations.find((c: any) => c.column1 === rowCol && c.column2 === colCol);
                            const val = cell?.value || 0;
                            const isSelected = (selection.x === rowCol && selection.y === colCol) || (selection.x === colCol && selection.y === rowCol);

                            // Color Logic
                            const bg = val > 0
                                ? `rgba(59, 130, 246, ${Math.max(0.1, val)})`
                                : `rgba(239, 68, 68, ${Math.max(0.1, Math.abs(val))})`;

                            return (
                                <div
                                    key={`${rowCol}-${colCol}`}
                                    onClick={() => onSelect(rowCol, colCol, val)}
                                    className={cn("w-16 h-12 border border-white flex items-center justify-center text-[10px] cursor-pointer transition-all hover:scale-105 hover:z-10 hover:shadow-lg",
                                        isSelected ? "ring-2 ring-black z-20" : "opacity-90 hover:opacity-100"
                                    )}
                                    style={{ backgroundColor: bg, color: Math.abs(val) > 0.5 ? 'white' : 'black' }}
                                    title={`${rowCol} vs ${colCol}: ${val.toFixed(2)}`}
                                >
                                    {cell ? val.toFixed(2) : '-'}
                                </div>
                            );
                        })}
                    </div>
                ))}

                <div className="mt-4 flex gap-6 text-xs text-gray-500 justify-end w-[calc(6rem*${uniqueCols.length}+6rem)]">
                    <div className="flex items-center gap-2">
                        <div className="h-2 w-16 bg-gradient-to-r from-blue-100 to-blue-600 rounded"></div>
                        <span>Positive Correlation</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="h-2 w-16 bg-gradient-to-r from-red-100 to-red-600 rounded"></div>
                        <span>Negative Correlation</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
